import { Navbar } from "../components/Navbar";
import { SustainingRoll } from "../components/SustainingRoll";
import { SustainingBand } from "../components/SustainingBand";
import { WaysToGive } from "../components/WaysToGive";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * /demo/sustaining-donors — mock-up of a quiet thank-you roll for monthly
 * General Fund donors, for founder review before anything is built.
 * Sample names only. Not linked from anywhere public.
 */
const SAMPLE = [
  { name: "Marcus T.", since: "Sep 2026", city: "Oakland, CA" },
  { name: "Anonymous", since: "Sep 2026" },
  { name: "Denise W.", since: "Sep 2026", city: "Atlanta, GA" },
  { name: "The Okafor family", since: "Oct 2026", city: "Houston, TX" },
  { name: "Priya R.", since: "Oct 2026", city: "Chicago, IL" },
  { name: "Anonymous", since: "Oct 2026" },
  { name: "James & Lena H.", since: "Nov 2026", city: "Durham, NC" },
  { name: "Coach Ray", since: "Nov 2026", city: "Baltimore, MD" },
  { name: "Aunt Vee", since: "Nov 2026", city: "Detroit, MI" },
  { name: "Samuel O.", since: "Dec 2026", city: "Brooklyn, NY" },
  { name: "Anonymous", since: "Dec 2026" },
  { name: "Rosa & Miguel A.", since: "Jan 2027", city: "San Antonio, TX" },
  { name: "Kendra B.", since: "Jan 2027", city: "Memphis, TN" },
  { name: "Class of '98, Howard", since: "Feb 2027" },
  { name: "Tomas E.", since: "Feb 2027", city: "Phoenix, AZ" },
];

const Note = ({ children }) => <p className="mt-3 max-w-2xl text-[13px] italic text-olive">{children}</p>;
const Label = ({ children }) => <p className="text-xs font-semibold uppercase tracking-[0.22em] text-olive">{children}</p>;

export default function SustainingDonorsMockup() {
  useNoIndex();
  return (
    <main data-testid="sustaining-donors-mockup" className="relative min-h-screen bg-sand text-ink">
      <Navbar onStart={() => {}} primaryLabel="Preview the platform" secondaryLabel={null} />

      <section className="mx-auto max-w-6xl px-6 pb-12 pt-32 lg:px-8 lg:pt-44">
        <span className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-cobalt">
          Sustaining donor wall · mock-up for review
        </span>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">
          A quiet <EmSwash>thank-you roll.</EmSwash>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-olive">
          Monthly General Fund donors rarely get a moment on the site: no campaign page, no progress bar, no wall. This is a place for them to feel seen between campaigns. Names only, never amounts. Three treatments below, then one in context.
        </p>
        <Note>Sample names. Nothing here is built or wired yet.</Note>
        <div className="mt-8 grid max-w-3xl grid-cols-1 gap-4 text-sm leading-relaxed text-ink sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-bone p-4">
            <Label>What would show</Label>
            <p className="mt-2">First name and last initial (or a nickname the donor types), an optional city, and the month they started. "Anonymous" for anyone who prefers it.</p>
          </div>
          <div className="rounded-xl border border-border bg-bone p-4">
            <Label>What would never show</Label>
            <p className="mt-2">Amounts, emails, full names by default, or any suggestion that a monthly gift is tied to a particular student. It is a gift to My Rich Aunt.</p>
          </div>
          <div className="rounded-xl border border-border bg-bone p-4">
            <Label>How names get there</Label>
            <p className="mt-2">A single opt-in on the monthly checkout: "List me on the sustaining donor roll as ___". Off by default. Removed automatically if the subscription is cancelled.</p>
          </div>
          <div className="rounded-xl border border-border bg-bone p-4">
            <Label>Where it could live</Label>
            <p className="mt-2">On the landing page directly under "Ways to give" (shown in D below), or as the right-hand column of the General Fund band on the donor demo.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-8 lg:px-8">
        <Label>A · The roll</Label>
        <Note>Names flow like a dedication page. Hover a name to see when they started. Warm, unhurried, no grid.</Note>
      </section>
      <SustainingRoll donors={SAMPLE} variant="roll" testid="sustaining-variant-roll" />

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-24 lg:px-8">
        <Label>B · The ledger</Label>
        <Note>A single ruled column, like the founding donors wall's quieter cousin. Reads as a record, not a leaderboard.</Note>
      </section>
      <SustainingRoll donors={SAMPLE} variant="ledger" testid="sustaining-variant-ledger" />

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-24 lg:px-8">
        <Label>C · The band (chosen · now live-wired)</Label>
        <Note>Compact, one line of names. Palette tint laid transparently over the footer watermark. Yellow is the default; the other four tints are shown beneath for comparison.</Note>
      </section>
      <SustainingBand donors={SAMPLE} tone="yellow" testid="sustaining-variant-band" />
      <div className="mx-auto max-w-6xl px-6 pt-10 lg:px-8"><Label>Alternate tints</Label></div>
      <div className="mt-4 space-y-4">
        {["tangerine", "cobalt", "grass", "blossom"].map((tone) => (
          <SustainingBand key={tone} donors={SAMPLE.slice(0, 8)} count={SAMPLE.length} tone={tone} testid={`sustaining-variant-band-${tone}`} />
        ))}
      </div>

      <section className="mx-auto max-w-6xl px-6 pb-6 pt-24 lg:px-8">
        <Label>D · In context, after "Ways to give"</Label>
        <Note>The roll (variant A) placed where a donor has just read about the fund. Scroll to see the rhythm.</Note>
      </section>
      <WaysToGive onDonate={() => {}} onBrowse={() => {}} />
      <SustainingRoll donors={SAMPLE} variant="roll" testid="sustaining-in-context" />

      <section className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        <Label>If you say yes, what gets built</Label>
        <ol className="mt-4 max-w-2xl list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink">
          <li>An opt-in field on the monthly General Fund checkout ("List me as ___"), stored with the recurring donation.</li>
          <li>A public read of active sustaining donors who opted in; cancelled subscriptions drop off automatically.</li>
          <li>One of the treatments above on the landing page, plus an admin toggle to hide any name.</li>
        </ol>
      </section>
    </main>
  );
}
